import Vue, { effectScope, nextTick, watch } from 'vue';
import { useDropZone } from '@vueuse/core';
import BaseDropArea from '@/components/base-components/components/drop-area/base-drop-area.vue';

function addClass(el, cls) {
    el.classList.add(cls);
}

function removeClass(el, cls) {
    el.classList.remove(cls);
}

function mountDropArea(options) {
    const vm = new Vue({
        data() {
            return {
                text: options.text || '',
                visible: false,
            };
        },
        render(h) {
            return h(BaseDropArea, {
                props: {
                    text: this.text,
                    visible: this.visible,
                },
            });
        },
    });

    vm.$mount();

    return {
        instance: vm,
        el: vm.$el,
        unmount: () => {
            vm.$destroy();
        },
    };
}

const insertDom = (parent, el) => {
    if (el.areaDomInserted) return;

    el.areaRelativeClassAdded = !parent.classList.contains('relative');

    if (el.areaRelativeClassAdded) {
        addClass(parent, 'relative');
    }

    parent.appendChild(el.areaMask);
    el.areaDomInserted = true;
};

const removeDom = (parent, el) => {
    if (!el.areaDomInserted || !el.areaMask?.parentNode) return;

    el.areaMask.parentNode.removeChild(el.areaMask);
    el.areaDomInserted = false;

    if (el.areaRelativeClassAdded) {
        removeClass(parent, 'relative');
        el.areaRelativeClassAdded = false;
    }
};

const hideArea = (el) => {
    if (!el.areaInstance) return;

    el.areaInstance.visible = false;
};

const updateArea = (el, binding) => {
    const options = binding.value || {};

    el.areaOptions = options;

    if (el.areaInstance) {
        el.areaInstance.text = options.text || '';
    }
};

const isEnabled = el => el.areaOptions?.enabled !== false;
const canDrop = (el, event) => el.areaOptions?.canDrop?.(event) ?? true;

const switchArea = (el, binding) => {
    if (binding.value?.enabled !== false) {
        nextTick(() => {
            if (!el.areaInstance) return;

            insertDom(el, el);
        });
    } else {
        hideArea(el);
        removeDom(el, el);
    }
};

const setupDropZone = (el) => {
    el.areaScope = effectScope();

    el.areaScope.run(() => {
        const { isOverDropZone } = useDropZone(el, {
            onOver: (files, event) => {
                if (!isEnabled(el) || !el.areaDomInserted || !canDrop(el, event)) {
                    hideArea(el);

                    return;
                }

                el.areaInstance.visible = true;
            },
            onDrop: (files, event) => {
                hideArea(el);

                if (!isEnabled(el) || !canDrop(el, event)) return;

                el.areaOptions?.onDrop?.(files ?? [], event);
            },
        });

        watch(isOverDropZone, (isOver) => {
            if (isOver) return;

            hideArea(el);
        });
    });
};

const directive = {
    bind(el, binding) {
        const mounted = mountDropArea(binding.value || {});

        el.areaInstance = mounted.instance;
        el.areaMask = mounted.el;
        el.areaUnmount = mounted.unmount;

        updateArea(el, binding);
        setupDropZone(el);

        if (binding.value?.enabled !== false) {
            switchArea(el, binding);
        }
    },

    update(el, binding) {
        updateArea(el, binding);

        if (binding.oldValue?.enabled !== binding.value?.enabled) {
            switchArea(el, binding);
        }
    },

    unbind(el) {
        el.areaScope?.stop();

        removeDom(el, el);
        el.areaUnmount?.();

        delete el.areaOptions;
        delete el.areaInstance;
        delete el.areaMask;
        delete el.areaUnmount;
        delete el.areaScope;
    },
};

export default directive;
